import React, { useState } from 'react';
import moment from 'moment';
import { View, Text, Pressable } from '../lib/react-native-compat';
import { pNum } from '../db/company';
import { resolveTier3 } from './billingTier3';
import { round2, weekStartOf, perDiemDays, DEFAULT_SETTINGS } from './billingRates';

// Labor QA checks for the Billing tab. No page of their own: Time Card
// Approval shows them in a time sheet's preview (and hands them to the
// Ticket Map's Claude review), and Summaries shows them on each summary's
// row, its open detail and the pre-export checklist.
//
// The last look before a time card goes out for signature or a summary goes
// out for billing: every person on the day (or on the summary) is checked
// for the four things that have actually gone wrong on cost-plus billing —
// hours charged to the wrong phase code, the wrong billed classification,
// a day that does not look like the person's other days, and per diem that
// is missing, doubled or paid at the wrong amount.
//
// Two scopes share one model. A "person-day" is one person's hours on one
// work date — cost codes and hours, straight/overtime split, classification
// (and where it came from), crew lead, shift, per diem. Time cards build
// person-days from HEET rows (the same readHEETLabor feed the Time Card
// Approval tracker uses), one date against the work day before it.
// Summaries build them from the summary's labor lines (the priced
// transactions billing.js already holds), with the job's other labor lines
// as the history.
//
// The checks themselves are pure functions over (current person-days,
// history person-days, ctx) so the rules are testable without a browser;
// each returns findings { level, tag, text, ids, lines } — ids map a finding
// back to person-days (flagsOf). Levels follow the pre-export
// checklist: fail = would bill wrong, warn = look at it, info = context.

export const AREAS = [
    { id: 'codes', title: 'Phase codes', desc: 'Hours land on the codes the person and their crew work' },
    { id: 'class', title: 'Classification', desc: 'Every person resolves to one rate-sheet classification with a rate' },
    { id: 'prev', title: 'Vs previous time cards', desc: 'New faces, missing faces, hours and shifts that changed' },
    { id: 'pd', title: 'Per diem', desc: 'Who has it, who lost it, and at what rate' },
];

// Phase codes the client never sees: hours on them fold into the person's
// largest other code on the field ticket, so they are worth a glance.
export const HIDDEN_CODES = ['100105'];

const LABOR_HOURS_TYPES = ['Regular', 'Overtime'];
const LABOR_CT = { '51100': 'st', '51200': 'ot', '51500': 'pd' };

const num = (n) => pNum(n) || 0;
const fH = (n) => (Math.round(num(n) * 100) / 100).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 2 });
const money = (n) => {
    const v = num(n);
    return `${v < 0 ? '-' : ''}$${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fDate = (d) => (d ? moment.utc(d, 'YYYY-MM-DD').format('ddd M/D') : '');
const plural = (n, w, ws) => `${n} ${n === 1 ? w : (ws || w + 's')}`;
const hasId = (p) => Boolean(p.id) && !String(p.id).startsWith('~') && String(p.id) !== String(p.name || '');
const ref = (p) => (hasId(p) ? `${p.name || p.id} (${p.id})` : p.name || String(p.id || ''));
// Distinct people in a list of person-days — a summary spans days, and
// "3 people" should not mean the same person three times.
const cnt = (people) => new Set((people || []).map((p) => p.id)).size;
// JDE's catch-all employee: salaried people whose time posts anonymously
// as one lump per day. Not a person — its hours never fit per-person rules.
export const isLump = (p) => String(p.id) === '999999' || String(p.name || '').trim() === 'Miscellaneous';
const codeList = (p) => Object.entries(p.codes).sort((a, b) => b[1] - a[1]).map(([c, h]) => `${c} ${fH(h)}h`).join(', ');
const median = (arr) => {
    if (!arr.length) return 0;
    const s = [...arr].sort((a, b) => a - b);
    const m = Math.floor(s.length / 2);
    return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};
const mode = (arr) => {
    const t = {};
    arr.forEach((v) => { t[v] = (t[v] || 0) + 1; });
    return Object.entries(t).sort((a, b) => b[1] - a[1] || Number(b[0]) - Number(a[0])).map(([v]) => Number(v))[0];
};

const blank = (d, id, name) => ({
    d, id: String(id), name: name || '', payClass: '', payCode: '', foreman: '', shift: '',
    codes: {}, st: 0, ot: 0, total: 0,
    pd: false, pdAmt: 0, pdN: 0, tp: false, rr: false,
    approved: true, raw: false, lump: false, src: '', inv: null,
    cls: '', clsSrc: 'none', fieldCls: '', mapCls: '', mapReason: '', noRate: false, rate: null,
});

// The classification every view bills a person under: the field's Staff
// List (staffCls) first, then the pay-class map (resolveTier3 — saved
// mapping, exact, title, truncated). Records where it came from, since a
// Staff List override that disagrees with the map is one of the findings.
const classify = (p, ctx) => {
    const m = resolveTier3(p.payClass, ctx.ratemap);
    const fieldCls = ctx.staffCls ? (ctx.staffCls[String(p.id)] || '') : '';
    p.mapCls = m.cls || '';
    p.mapReason = m.reason;
    p.fieldCls = fieldCls;
    // A priced line already knows what it billed as (clsSrc 'line'/'map');
    // a HEET person resolves here — Staff List first, then the map.
    if (!p.cls) {
        if (fieldCls) { p.cls = fieldCls; p.clsSrc = 'field'; }
        else if (m.cls) { p.cls = m.cls; p.clsSrc = m.reason; }
        else p.clsSrc = 'none';
    }
    const rate = p.cls && ctx.rateByCls ? ctx.rateByCls[p.cls] : null;
    p.rate = rate ? { st: num(rate.st), ot: num(rate.ot) } : null;
    if (p.cls && !p.rate && ctx.rateByCls) p.noRate = true;
    if (!p.cls) p.noRate = true;
    return p;
};

/**
 * One date's HEET rows → person-days. Same Record_Type discipline as
 * ticketFrom: only Regular/Overtime carry hours (Calculated_Hours per type);
 * Per Diem is a money-only row counted, not summed into hours. Shift comes
 * from the HeavyJob card (1 day / 2 night) unless ctx.overrides pins it —
 * the roster + manual corrections the tracker resolves.
 */
export const personDaysFromHeet = (rows, date, ctx = {}) => {
    const by = {};
    (rows || []).forEach((r) => {
        if (r.Record_Type === 'Equipment' || !r.Staff_ID) return;
        const id = String(r.Staff_ID);
        const p = by[id] || (by[id] = { ...blank(date, id, r.Staff_Name), src: ctx.source || 'HEET' });
        if (!p.name && r.Staff_Name) p.name = r.Staff_Name;
        if (!p.payClass && r.Pay_Class_Description) p.payClass = r.Pay_Class_Description;
        if (!p.payCode && r.Pay_Class_Code) p.payCode = r.Pay_Class_Code;
        if (!p.foreman && r.Foreman_Name) p.foreman = r.Foreman_Name;
        if (Number(r.Shift) === 2) p.shift = 'Night';
        else if (Number(r.Shift) === 1 && p.shift !== 'Night') p.shift = 'Day';
        if (Number(r.Is_Approved) === 0) p.approved = false;
        if (String(r.Hour_Tag_Code || '') === 'HJRAW') p.raw = true;
        if (r.Record_Type === 'Per Diem') { p.pd = true; p.pdN += 1; p.pdAmt += num(r.Amount); return; }
        if (r.Record_Type === 'Truck Pay') { p.tp = true; return; }
        if (r.Record_Type === 'Rig Pay') { p.rr = true; return; }
        if (!LABOR_HOURS_TYPES.includes(r.Record_Type)) return;
        const h = num(r.Hours);
        if (!h) return;
        if (r.Record_Type === 'Overtime') p.ot += h; else p.st += h;
        p.total += h;
        if (r.Cost_Code) p.codes[r.Cost_Code] = (p.codes[r.Cost_Code] || 0) + h;
    });
    const ov = ctx.overrides || {};
    return Object.values(by).map((p) => {
        if (ov[p.id] === 'Night' || ov[p.id] === 'Day') p.shift = ov[p.id];
        p.pdAmt = round2(p.pdAmt);
        p.lump = isLump(p);
        return classify(p, ctx);
    }).sort((a, b) => String(a.name).localeCompare(String(b.name)));
};

/**
 * Priced transaction lines (billing.js txns, or a summary's snapshot
 * fallback) → person-days. 51100 is straight time, 51200 overtime, 51500 the
 * per diem money; everything else on a summary is not a person's day.
 * People key on Staff_ID when the line carries it, else the name — the
 * snapshot fallback only froze the name.
 */
export const personDaysFromLines = (lines, ctx = {}) => {
    const by = {};
    (lines || []).forEach((t) => {
        const kind = LABOR_CT[String(t.ct || '')];
        if (!kind) return;
        const date = t.date ? moment.utc(t.date).format('YYYY-MM-DD') : '';
        if (!date) return;
        const id = String(t.sid || t.who || '').trim();
        if (!id) return;
        const k = `${id}|${date}`;
        const p = by[k] || (by[k] = { ...blank(date, id, t.who), src: 'Cost history', inv: t.inv || null });
        if (!p.payClass && t.jde) p.payClass = t.jde;
        // A per-line classification fix (lcOv) is the strongest source; the
        // job's map (ba) otherwise.
        if (t.lcOv) { p.cls = t.lcOv; p.clsSrc = 'line'; }
        else if (!p.cls && t.ba) { p.cls = t.ba; p.clsSrc = 'map'; }
        if (t.noRate) p.noRate = true;
        if (kind === 'pd') { p.pd = true; p.pdN += 1; p.pdAmt += num(t.cost); return; }
        const h = num(t.hrs);
        if (kind === 'ot') p.ot += h; else p.st += h;
        p.total += h;
        if (t.cc) p.codes[t.cc] = (p.codes[t.cc] || 0) + h;
    });
    return Object.values(by).map((p) => {
        p.pdAmt = round2(p.pdAmt);
        p.lump = isLump(p);
        return classify(p, ctx);
    }).sort((a, b) => String(a.d).localeCompare(String(b.d)) || String(a.name).localeCompare(String(b.name)));
};

// history grouped per person, oldest first.
const histBy = (hist) => {
    const m = {};
    (hist || []).forEach((p) => { (m[p.id] = m[p.id] || []).push(p); });
    Object.values(m).forEach((l) => l.sort((a, b) => String(a.d).localeCompare(String(b.d))));
    return m;
};
const last = (list) => (list && list.length ? list[list.length - 1] : null);

const item = (level, tag, text, people, lines) => ({
    level, tag, text,
    ids: (people || []).map((p) => `${p.id}|${p.d}`),
    lines: [...new Set(lines || (people || []).map(ref))],
});

// ── Phase codes ──
export const checkPhaseCodes = (cur, hist, ctx = {}) => {
    const items = [];
    const hb = histBy(hist);
    const known = new Set([...(ctx.jobCodes || [])]);
    (hist || []).forEach((p) => Object.keys(p.codes).forEach((c) => known.add(c)));
    const lumps = cur.filter((p) => p.lump);
    const worked = cur.filter((p) => p.total > 0 && !p.lump);

    // Codes the job has never posted to at all — a typo on the card, or a
    // brand-new phase nobody has told billing about.
    if (known.size) {
        const hit = worked.filter((p) => Object.keys(p.codes).some((c) => !known.has(c)));
        if (hit.length) {
            items.push(item('warn', 'new code (job)',
                `${plural(cnt(hit), 'person', 'people')} charged to a phase code never seen on this job before`,
                hit, hit.map((p) => `${ref(p)} · ${Object.keys(p.codes).filter((c) => !known.has(c)).map((c) => `${c} ${fH(p.codes[c])}h`).join(', ')}`)));
        }
    }
    // Codes new to the person — they have a track record and this is off it.
    const drift = worked.filter((p) => {
        const h = hb[p.id];
        if (!h || h.length < 2) return false;
        const mine = new Set();
        h.forEach((x) => Object.keys(x.codes).forEach((c) => mine.add(c)));
        return Object.keys(p.codes).some((c) => !mine.has(c));
    });
    if (drift.length) {
        items.push(item('warn', 'new code',
            `${plural(cnt(drift), 'person', 'people')} on a phase code they have not worked in the loaded history`,
            drift, drift.map((p) => {
                const mine = new Set();
                hb[p.id].forEach((x) => Object.keys(x.codes).forEach((c) => mine.add(c)));
                return `${ref(p)} · ${Object.keys(p.codes).filter((c) => !mine.has(c)).map((c) => `${c} ${fH(p.codes[c])}h`).join(', ')} (usually ${[...mine].join(', ')})`;
            })));
    }
    // Off-crew coding: everyone else under the same crew lead worked other
    // codes entirely.
    const crews = {};
    worked.forEach((p) => { if (p.foreman) (crews[p.foreman] = crews[p.foreman] || []).push(p); });
    const offCrew = [];
    Object.values(crews).forEach((crew) => {
        if (crew.length < 3) return;
        crew.forEach((p) => {
            const others = new Set();
            crew.forEach((q) => { if (q !== p) Object.keys(q.codes).forEach((c) => others.add(c)); });
            if (Object.keys(p.codes).length && !Object.keys(p.codes).some((c) => others.has(c))) offCrew.push(p);
        });
    });
    if (offCrew.length) {
        items.push(item('warn', 'off-crew code',
            `${plural(cnt(offCrew), 'person', 'people')} coded to phases nobody else on their crew touched`,
            offCrew, offCrew.map((p) => `${ref(p)} · ${codeList(p)} · crew ${p.foreman}`)));
    }
    const split = worked.filter((p) => Object.keys(p.codes).length >= 4);
    if (split.length) {
        items.push(item('warn', 'split 4+',
            `${plural(cnt(split), 'person', 'people')} split across four or more phase codes in one day`,
            split, split.map((p) => `${ref(p)} · ${codeList(p)}`)));
    }
    const long = worked.filter((p) => p.total > 14);
    if (long.length) {
        items.push(item('warn', 'over 14h', `${plural(cnt(long), 'person', 'people')} over 14 hours in a day`,
            long, long.map((p) => `${ref(p)} · ${fH(p.total)}h`)));
    }
    const odd = worked.filter((p) => Math.abs(p.total * 4 - Math.round(p.total * 4)) > 0.001);
    if (odd.length) {
        items.push(item('warn', 'odd hours', `${plural(cnt(odd), 'person', 'people')} with hours not on the quarter hour`,
            odd, odd.map((p) => `${ref(p)} · ${fH(p.total)}h`)));
    }
    const short = worked.filter((p) => p.total < 4);
    if (short.length) {
        items.push(item('info', 'under 4h', `${plural(cnt(short), 'person', 'people')} under 4 hours (orientation, partial day?)`,
            short, short.map((p) => `${ref(p)} · ${fH(p.total)}h`)));
    }
    const hidden = ctx.hiddenCodes || HIDDEN_CODES;
    const hid = worked.filter((p) => Object.keys(p.codes).some((c) => hidden.includes(c)));
    if (hid.length) {
        items.push(item('info', hidden.join('/'),
            `${plural(cnt(hid), 'person', 'people')} with hours on ${hidden.join('/')} — hidden on client tickets, folds into their largest code`,
            hid, hid.map((p) => `${ref(p)} · ${hidden.filter((c) => p.codes[c]).map((c) => `${c} ${fH(p.codes[c])}h`).join(', ')}`)));
    }
    // HeavyJob vs HEET disagreement on the day: HEET double the card is a
    // double load, HEET under it the morning's partial pull.
    const val = (ctx.validation || []).filter((v) => !cur.length || cur.some((p) => p.d === v.Work_Date));
    if (val.length) {
        const dbl = val.filter((v) => v.Kind === 'Hours differ' && num(v.HJ_Hours) > 0 && Math.abs(num(v.HEET_Hours) / num(v.HJ_Hours) - 2) < 0.05);
        const rest = val.filter((v) => !dbl.includes(v));
        const idsOf = (list) => list.map((v) => ({ id: String(v.Staff_ID), d: v.Work_Date }));
        if (dbl.length) {
            items.push({ ...item('fail', 'double load', `${plural(cnt(dbl), 'person', 'people')} carry exactly double their HeavyJob hours in HEET — a double load`, idsOf(dbl)),
                lines: dbl.map((v) => `${v.Staff_Name || v.Staff_ID} · HEET ${fH(v.HEET_Hours)}h vs HeavyJob ${fH(v.HJ_Hours)}h`) });
        }
        if (rest.length) {
            items.push({ ...item('warn', 'HJ≠HEET', `${plural(cnt(rest), 'person', 'people')} differ between HeavyJob and HEET on this day`, idsOf(rest)),
                lines: rest.map((v) => `${v.Staff_Name || v.Staff_ID} · ${v.Kind}: HEET ${fH(v.HEET_Hours)}h vs HeavyJob ${fH(v.HJ_Hours)}h`) });
        }
    }
    const raw = cur.filter((p) => p.raw);
    if (raw.length) {
        const all = raw.length === cur.length;
        items.push({
            ...item('info', all ? '' : 'HJ raw', `${all ? 'Everyone' : plural(cnt(raw), 'person', 'people')} from the HeavyJob raw feed only — straight time, no OT or per diem split until HEET pulls the day`, raw),
            lines: all ? [] : raw.map(ref),
        });
    }
    if (lumps.length) {
        items.push(item('info', 'lump', `JDE Miscellaneous (999999) lump — ${fH(lumps.reduce((t, p) => t + p.total, 0))}h of salaried time posting anonymously, not checked as a person`,
            lumps, lumps.map((p) => `${fDate(p.d)} · ${codeList(p)}`)));
    }
    const unapproved = cur.filter((p) => !p.approved && !p.raw);
    if (unapproved.length) {
        items.push(item('info', 'unapproved', `${plural(cnt(unapproved), 'person', 'people')} on timecards not yet approved in HeavyJob`, unapproved));
    }
    if (ctx.leftOff && ctx.leftOff.length) {
        const lo = ctx.leftOff;
        items.push(item('warn', 'left off',
            `${plural(lo.length, 'person-day')} with unbilled labor on these dates not on this summary (${fH(lo.reduce((s, p) => s + p.total, 0))}h)`,
            lo, lo.map((p) => `${fDate(p.d)} · ${ref(p)} · ${codeList(p) || 'per diem only'}`)));
    }
    const totals = {};
    worked.concat(lumps).forEach((p) => Object.entries(p.codes).forEach(([c, h]) => { totals[c] = (totals[c] || 0) + h; }));
    const top = Object.entries(totals).sort((a, b) => b[1] - a[1]);
    if (top.length) {
        items.push({ level: 'info', tag: '', ids: [], lines: [],
            text: `${plural(top.length, 'phase code')} worked · ${top.slice(0, 4).map(([c, h]) => `${c} ${fH(h)}h`).join(' · ')}${top.length > 4 ? ' · …' : ''}` });
    } else if (!items.length) {
        items.push({ level: 'info', tag: '', ids: [], lines: [], text: 'No hours to check' });
    }
    return items;
};

// ── Classification ──
export const checkClassification = (curAll, hist, ctx = {}) => {
    const items = [];
    const hb = histBy(hist);
    const cur = curAll.filter((p) => !p.lump);
    const none = cur.filter((p) => !p.cls);
    if (none.length) {
        items.push(item('fail', 'no class', `${plural(cnt(none), 'person', 'people')} with no billable classification — ${plural(none.length, 'line')} would price at $0`,
            none, none.map((p) => `${ref(p)} · pay class "${p.payClass || '?'}"${p.mapReason === 'non-field' ? ' (corporate title)' : ''}`)));
    }
    const noRate = cur.filter((p) => p.cls && p.noRate);
    if (noRate.length) {
        items.push(item('fail', 'no rate', `${plural(cnt(noRate), 'person', 'people')} classified, but the rate schedule has no rate for that classification`,
            noRate, noRate.map((p) => `${ref(p)} · ${p.cls}`)));
    }
    // The field's Staff List names the person's billed classification. On a
    // time card it wins, so a disagreement is with the pay-class map; on a
    // summary the line billed at the map's class, so the disagreement is
    // with what actually priced.
    const dis = cur.filter((p) => p.fieldCls && ((p.cls && p.cls !== p.fieldCls) || (p.mapCls && p.mapCls !== p.fieldCls)));
    if (dis.length) {
        items.push(item('warn', 'field≠map', `${plural(cnt(dis), 'person', 'people')} where the field's Staff List disagrees with the pay-class map`,
            dis, dis.map((p) => (p.cls && p.cls !== p.fieldCls
                ? `${ref(p)} · bills as "${p.cls}" but Staff List says "${p.fieldCls}" (pay class ${p.payClass})`
                : `${ref(p)} · Staff List "${p.fieldCls}" vs map "${p.mapCls}" — Staff List wins (pay class ${p.payClass})`))));
    }
    const nonField = cur.filter((p) => p.mapReason === 'non-field' && p.total > 0 && p.cls);
    if (nonField.length) {
        items.push(item('warn', 'corporate title', `${plural(cnt(nonField), 'person', 'people')} on a corporate pay class with field hours`,
            nonField, nonField.map((p) => `${ref(p)} · ${p.payClass} → bills as ${p.cls}`)));
    }
    const changed = cur.filter((p) => { const l = last(hb[p.id]); return l && l.cls && p.cls && l.cls !== p.cls; });
    if (changed.length) {
        items.push(item('warn', 'class changed', `${plural(cnt(changed), 'person', 'people')} billing under a different classification than their previous day`,
            changed, changed.map((p) => `${ref(p)} · ${last(hb[p.id]).cls} (${fDate(last(hb[p.id]).d)}) → ${p.cls}`)));
    }
    const pcChanged = cur.filter((p) => { const l = last(hb[p.id]); return l && l.payClass && p.payClass && l.payClass !== p.payClass && !changed.includes(p); });
    if (pcChanged.length) {
        items.push(item('info', 'pay class changed', `${plural(cnt(pcChanged), 'person', 'people')} whose payroll class changed (same billed classification)`,
            pcChanged, pcChanged.map((p) => `${ref(p)} · ${last(hb[p.id]).payClass} → ${p.payClass}`)));
    }
    // One pay class billing as two classifications on the same day — usually
    // Staff List overrides, worth a look either way.
    const byPc = {};
    cur.forEach((p) => { if (p.payClass && p.cls) (byPc[p.payClass] = byPc[p.payClass] || {})[p.cls] = (byPc[p.payClass][p.cls] || 0) + 1; });
    const incons = Object.entries(byPc).filter(([, m]) => Object.keys(m).length > 1);
    if (incons.length) {
        const people = cur.filter((p) => incons.some(([pc]) => pc === p.payClass));
        items.push(item('warn', 'mixed map', `${plural(incons.length, 'pay class', 'pay classes')} billing under more than one classification today`,
            people, incons.map(([pc, m]) => `${pc} → ${Object.entries(m).map(([c, n]) => `${c} (${n})`).join(', ')}`)));
    }
    if (ctx.staffCls && Object.keys(ctx.staffCls).length) {
        const off = cur.filter((p) => !p.fieldCls && p.total > 0);
        if (off.length) {
            items.push(item('info', 'not on Staff List', `${plural(cnt(off), 'person', 'people')} not on the field's Staff List — classified from the pay class map`,
                off, off.map((p) => `${ref(p)} · ${p.payClass} → ${p.cls || '—'}`)));
        }
    }
    const src = {};
    cur.forEach((p) => { src[p.clsSrc] = (src[p.clsSrc] || 0) + 1; });
    const srcLabel = { field: 'Staff List', map: 'saved map', line: 'line override', exact: 'exact', title: 'staff title', truncated: 'truncated match', alias: 'alias', none: 'unresolved' };
    const used = new Set(cur.map((p) => p.cls).filter(Boolean));
    items.push({ level: 'info', tag: '', ids: [], lines: [],
        text: `${plural(used.size, 'classification')} in use · sources: ${Object.entries(src).map(([k, n]) => `${srcLabel[k] || k} ${n}`).join(', ') || 'none'}` });
    return items;
};

// ── Vs previous time cards ──
export const checkVsPrevious = (curIn, hist, ctx = {}) => {
    const items = [];
    const cur = curIn.filter((p) => !p.lump);
    if (!hist || !hist.length) {
        items.push({ level: 'info', tag: '', ids: [], lines: [], text: 'No earlier days loaded — nothing to compare against' });
        return items;
    }
    const hb = histBy(hist);
    const curDate = cur.length ? cur.map((p) => p.d).sort()[0] : '';
    const prevDate = hist.map((p) => p.d).filter((d) => !curDate || d < curDate).sort().pop() || '';
    const prevDay = hist.filter((p) => p.d === prevDate);
    const curIds = new Set(cur.map((p) => p.id));

    const fresh = cur.filter((p) => !hb[p.id] && p.total > 0);
    if (fresh.length) {
        items.push(item('info', 'new', `${plural(cnt(fresh), 'person', 'people')} not on any earlier loaded day`,
            fresh, fresh.map((p) => `${ref(p)} · ${p.cls || p.payClass} · ${fH(p.total)}h${p.foreman ? ` · crew ${p.foreman}` : ''}`)));
    }
    const gone = prevDay.filter((p) => !curIds.has(p.id) && p.total > 0);
    if (gone.length) {
        items.push(item('info', 'absent', `${plural(cnt(gone), 'person', 'people')} on ${fDate(prevDate)} with no time today`,
            gone, gone.map((p) => `${ref(p)} · ${p.cls || p.payClass}${p.foreman ? ` · crew ${p.foreman}` : ''}`)));
    }
    const hrs = cur.filter((p) => {
        const h = (hb[p.id] || []).filter((x) => x.total > 0);
        if (h.length < 2 || !p.total) return false;
        return Math.abs(p.total - median(h.map((x) => x.total))) > 4;
    });
    if (hrs.length) {
        items.push(item('warn', 'hours vs usual', `${plural(cnt(hrs), 'person', 'people')} more than 4 hours off their usual day`,
            hrs, hrs.map((p) => `${ref(p)} · ${fH(p.total)}h today vs usual ${fH(median(hb[p.id].filter((x) => x.total > 0).map((x) => x.total)))}h`)));
    }
    const shift = cur.filter((p) => { const l = last(hb[p.id]); return l && l.shift && p.shift && l.shift !== p.shift; });
    if (shift.length) {
        items.push(item('warn', 'shift changed', `${plural(cnt(shift), 'person', 'people')} switched shift since their previous day`,
            shift, shift.map((p) => `${ref(p)} · ${last(hb[p.id]).shift} (${fDate(last(hb[p.id]).d)}) → ${p.shift}`)));
    }
    const crew = cur.filter((p) => { const l = last(hb[p.id]); return l && l.foreman && p.foreman && l.foreman !== p.foreman; });
    if (crew.length) {
        items.push(item('info', 'crew changed', `${plural(cnt(crew), 'person', 'people')} under a different crew lead than their previous day`,
            crew, crew.map((p) => `${ref(p)} · ${last(hb[p.id]).foreman} → ${p.foreman}`)));
    }
    const ot = cur.filter((p) => { const l = last(hb[p.id]); return l && p.ot > 0 && !l.ot && l.total >= p.total; });
    if (ot.length) {
        items.push(item('info', 'OT new', `${plural(cnt(ot), 'person', 'people')} with overtime today and none on their previous day of the same length`,
            ot, ot.map((p) => `${ref(p)} · ${fH(p.ot)}h OT of ${fH(p.total)}h`)));
    }
    if (prevDay.length) {
        const h0 = prevDay.reduce((s, p) => s + p.total, 0);
        const h1 = cur.reduce((s, p) => s + p.total, 0);
        const n0 = prevDay.filter((p) => p.total > 0).length;
        const n1 = cur.filter((p) => p.total > 0).length;
        const big = h0 > 0 && Math.abs(h1 - h0) / h0 > 0.25;
        items.push({ level: big ? 'warn' : 'info', tag: '', ids: [], lines: [],
            text: `Today ${n1} people · ${fH(h1)}h vs ${fDate(prevDate)} ${n0} people · ${fH(h0)}h (${h1 >= h0 ? '+' : ''}${fH(h1 - h0)}h)${big ? ' — more than a quarter different' : ''}` });
    }
    return items;
};

// ── Per diem ──
export const checkPerDiem = (curAll, hist, ctx = {}) => {
    const items = [];
    const hb = histBy(hist);
    // The HeavyJob raw feed has no per diem split — nothing to judge until
    // HEET pulls the day. Raw people drop out of every rule below and the
    // reader is told why instead of seeing a wall of "lost per diem".
    const rawN = curAll.filter((p) => p.raw && !p.lump).length;
    const cur = curAll.filter((p) => !p.lump && !p.raw);
    if (rawN) {
        items.push({ level: 'info', tag: '', ids: [], lines: [],
            text: `${rawN === curAll.length ? 'Everyone' : plural(rawN, 'person', 'people')} on the HeavyJob raw feed — per diem is not in it yet, so it is not checked${rawN === curAll.length ? ' today' : ' for them'}` });
    }
    const policyRate = round2(num(ctx.pdRate));
    const amounts = cur.concat(hist || []).filter((p) => p.pd && p.pdAmt > 0).map((p) => round2(p.pdAmt));
    const usual = amounts.length ? mode(amounts) : 0;
    const expect = policyRate || usual;

    const dbl = cur.filter((p) => p.pdN > 1);
    if (dbl.length) {
        items.push(item('fail', 'double PD', `${plural(cnt(dbl), 'person', 'people')} with more than one per diem entry on the day`,
            dbl, dbl.map((p) => `${ref(p)} · ${p.pdN} entries · ${money(p.pdAmt)}`)));
    }
    const noHours = cur.filter((p) => p.pd && !p.total);
    if (noHours.length) {
        items.push(item('warn', 'PD no hours', `${plural(cnt(noHours), 'person', 'people')} paid per diem with no hours — allowed only as a 6th/7th day of a full week`,
            noHours, noHours.map((p) => `${ref(p)} · ${money(p.pdAmt)}`)));
    }
    const lost = cur.filter((p) => {
        if (p.pd || !p.total) return false;
        const h = (hb[p.id] || []).filter((x) => x.total > 0);
        return h.length >= 2 && h.every((x) => x.pd);
    });
    if (lost.length) {
        items.push(item('warn', 'PD missing', `${plural(cnt(lost), 'person', 'people')} with hours but no per diem, who drew it on every earlier loaded day`,
            lost, lost.map((p) => `${ref(p)} · ${fH(p.total)}h · had ${money(last(hb[p.id].filter((x) => x.pd)).pdAmt)} on ${fDate(last(hb[p.id].filter((x) => x.pd)).d)}`)));
    }
    // The VoltaGrid rubric (9/25): someone who works without per diem but
    // stayed in a hotel we paid for bills that stay as per diem — $175 a
    // day, never the hotel bill. Nothing here knows who stayed, so list
    // who worked without per diem for the person who does.
    if (ctx.hotelPd) {
        const noPd = cur.filter((p) => !p.pd && p.total > 0);
        if (noPd.length) {
            items.push(item('info', 'no PD', `${plural(cnt(noPd), 'person', 'people')} with hours and no per diem — if we put any of them up, bill the stay as per diem at ${money(expect || 175)} a day, not the hotel bill`,
                noPd, noPd.map((p) => `${ref(p)} · ${fH(p.total)}h`)));
        }
    }
    const gained = cur.filter((p) => {
        if (!p.pd) return false;
        const h = (hb[p.id] || []).filter((x) => x.total > 0);
        return h.length >= 2 && !h.some((x) => x.pd);
    });
    if (gained.length) {
        items.push(item('warn', 'PD new', `${plural(cnt(gained), 'person', 'people')} drawing per diem today who never had it on the earlier loaded days`,
            gained, gained.map((p) => `${ref(p)} · ${money(p.pdAmt)}`)));
    }
    if (expect > 0) {
        const off = cur.filter((p) => p.pd && p.pdAmt > 0 && Math.abs(round2(p.pdAmt) - expect) > 0.01);
        if (off.length) {
            items.push(item('warn', 'PD rate', `${plural(cnt(off), 'person', 'people')} paid per diem at other than ${money(expect)}${policyRate ? ' (job policy rate)' : ' (the usual amount)'}`,
                off, off.map((p) => `${ref(p)} · ${money(p.pdAmt)}`)));
        }
    }
    const amtChanged = cur.filter((p) => {
        if (!p.pd || !p.pdAmt) return false;
        const l = last((hb[p.id] || []).filter((x) => x.pd && x.pdAmt > 0));
        return l && Math.abs(round2(l.pdAmt) - round2(p.pdAmt)) > 0.01;
    });
    if (amtChanged.length) {
        items.push(item('warn', 'PD amt changed', `${plural(cnt(amtChanged), 'person', 'people')} whose per diem amount changed from their previous day`,
            amtChanged, amtChanged.map((p) => { const l = last(hb[p.id].filter((x) => x.pd && x.pdAmt > 0)); return `${ref(p)} · ${money(l.pdAmt)} → ${money(p.pdAmt)}`; })));
    }
    // Crews mixing per diem and none — often right (locals beside
    // travelers), but a crew that is all-but-one is worth the glance.
    const crews = {};
    cur.filter((p) => p.total > 0 && p.foreman).forEach((p) => { (crews[p.foreman] = crews[p.foreman] || []).push(p); });
    const lone = [];
    Object.entries(crews).forEach(([f, crew]) => {
        if (crew.length < 4) return;
        const without = crew.filter((p) => !p.pd);
        const withPd = crew.filter((p) => p.pd);
        if (without.length === 1 && withPd.length >= 3) lone.push({ p: without[0], f, kind: 'without' });
        if (withPd.length === 1 && without.length >= 3) lone.push({ p: withPd[0], f, kind: 'with' });
    });
    if (lone.length) {
        items.push(item('info', 'crew odd one', `${plural(cnt(lone), 'person', 'people')} the odd one out on per diem within their crew`,
            lone.map((x) => x.p), lone.map((x) => `${ref(x.p)} · ${x.kind === 'without' ? 'no per diem' : 'per diem'} while the rest of crew ${x.f} ${x.kind === 'without' ? 'has it' : 'does not'}`)));
    }
    // Multi-day scope (a summary): weekly policy — days paid vs days earned.
    const dates = new Set(cur.map((p) => p.d));
    if (dates.size > 1 && ctx.pdPolicy) {
        const ws = ctx.pdPolicy.ws === undefined || ctx.pdPolicy.ws === null || ctx.pdPolicy.ws === '' ? DEFAULT_SETTINGS.pd.ws : (num(ctx.pdPolicy.ws) ? 1 : 0);
        const weeks = {};
        cur.forEach((p) => {
            const wk = weekStartOf(p.d, ws);
            const k = `${p.id}|${wk}`;
            const w = weeks[k] || (weeks[k] = { p, wk, worked: new Set(), paid: 0, days: [] });
            if (p.total > 0) w.worked.add(p.d);
            if (p.pd) w.paid += 1;
            w.days.push(p);
        });
        const short = []; const over = [];
        Object.values(weeks).forEach((w) => {
            const earn = perDiemDays(w.worked.size, ctx.pdPolicy);
            // Only judge weeks the summary holds in full — a partial week
            // (the summary's first or last) can't be short.
            const full = ctx.fullWeeks ? ctx.fullWeeks.has(w.wk) : true;
            if (!full) return;
            if (w.paid && w.paid < earn) short.push({ ...w, earn });
            if (w.paid > earn) over.push({ ...w, earn });
        });
        if (over.length) {
            items.push(item('warn', 'PD over policy', `${plural(over.length, 'person-week')} paid more per diem days than the policy earns`,
                over.flatMap((w) => w.days), over.map((w) => `${ref(w.p)} · week of ${fDate(w.wk)} · ${w.paid} paid vs ${w.earn} earned (${w.worked.size} worked)`)));
        }
        if (short.length) {
            items.push(item('warn', 'PD under policy', `${plural(short.length, 'person-week')} paid fewer per diem days than the policy earns`,
                short.flatMap((w) => w.days), short.map((w) => `${ref(w.p)} · week of ${fDate(w.wk)} · ${w.paid} paid vs ${w.earn} earned (${w.worked.size} worked)`)));
        }
    }
    const worked = cur.filter((p) => p.total > 0);
    const withPd = cur.filter((p) => p.pd);
    if (worked.length) {
        const pct = Math.round((worked.filter((p) => p.pd).length / worked.length) * 100);
        const multi = new Set(cur.map((p) => p.d)).size > 1;
        items.push({ level: 'info', tag: '', ids: [], lines: [],
            text: `${withPd.length} of ${multi ? plural(worked.length, 'person-day') : plural(cnt(worked), 'person', 'people')} with per diem (${pct}%) · ${money(withPd.reduce((s, p) => s + p.pdAmt, 0))}${expect ? ` · ${policyRate ? 'policy' : 'usual'} ${money(expect)}/day` : ''}` });
    }
    return items;
};

export const worstLevel = (items) => ['fail', 'warn'].find((l) => items.some((x) => x.level === l)) || 'pass';

export const runChecks = (cur, hist, ctx = {}) => {
    const run = { codes: checkPhaseCodes, class: checkClassification, prev: checkVsPrevious, pd: checkPerDiem };
    return AREAS.map((a) => {
        let items;
        try { items = run[a.id](cur, hist, ctx); } catch (e) { items = [{ level: 'fail', tag: 'error', text: `Check errored — ${e?.message || e}`, ids: [], lines: [] }]; }
        return { ...a, items, status: worstLevel(items) };
    });
};

// person-day key → [{ level, tag, area }] for the grid's FLAGS column.
export const flagsOf = (areas) => {
    const m = {};
    (areas || []).forEach((a) => (a.items || []).forEach((it) => {
        if (it.level === 'info' && !it.tag) return;
        (it.ids || []).forEach((k) => { (m[k] = m[k] || []).push({ level: it.level, tag: it.tag, area: a.id }); });
    }));
    return m;
};

// ── Summaries ──

/**
 * One summary's labor QA — the Summaries row, its open detail and the
 * pre-export checklist all read this. lines are the summary's own lines
 * (live, or billing.js's snapshot fallback); txns every priced line on the
 * job. History is the job's other labor before the summary's first date;
 * leftOff the unbilled person-days on its own dates (a day that probably
 * belongs on it); fullWeeks the pay weeks it covers end to end, where the
 * per diem minimums apply. Null when the summary carries no labor.
 */
export const summaryQa = (no, lines, txns, ctx = {}) => {
    const cur = personDaysFromLines(lines, ctx);
    if (!cur.length) return null;
    const dates = new Set(cur.map((p) => p.d));
    const sorted = [...dates].sort();
    const others = (txns || []).filter((t) => t.inv !== no && LABOR_CT[String(t.ct || '')] && !t.nb);
    const otherDays = personDaysFromLines(others, ctx);
    const hist = otherDays.filter((p) => p.d < sorted[0]);
    const leftOff = personDaysFromLines(others.filter((t) => !t.inv), ctx).filter((p) => dates.has(p.d));
    const pdPolicy = ctx.pdPolicy || DEFAULT_SETTINGS.pd;
    const ws = pdPolicy.ws === undefined || pdPolicy.ws === null || pdPolicy.ws === '' ? DEFAULT_SETTINGS.pd.ws : (num(pdPolicy.ws) ? 1 : 0);
    const jobDates = new Set(otherDays.map((p) => p.d));
    const fullWeeks = new Set(sorted.map((d) => weekStartOf(d, ws)).filter((wk) => {
        for (let i = 0; i < 7; i += 1) {
            const d = moment.utc(wk, 'YYYY-MM-DD').add(i, 'day').format('YYYY-MM-DD');
            if (jobDates.has(d) && !dates.has(d)) return false;
        }
        return true;
    }));
    const areas = runChecks(cur, hist, { ...ctx, leftOff, fullWeeks });
    return { cur, hist, areas, dates: sorted };
};

// Fail / check finding counts across the areas.
export const qaTally = (areas) => {
    const t = { fail: 0, warn: 0 };
    (areas || []).forEach((a) => (a.items || []).forEach((x) => { if (x.level === 'fail' || x.level === 'warn') t[x.level] += 1; }));
    return t;
};

// The areas flattened into one checklist step's items, area named up front.
export const qaItems = (areas) => (areas || []).flatMap((a) => (a.items || [])
    .map((x) => ({ level: x.level, text: `${a.title} — ${x.text}`, lines: x.lines })));

// ── UI ──

const TONE = {
    pass: { color: '#15803d', glyph: '✓', label: 'PASS' },
    warn: { color: '#b45309', glyph: '!', label: 'CHECK' },
    fail: { color: '#b91c1c', glyph: '✕', label: 'FAIL' },
    info: { color: '#64748b', glyph: '·', label: '' },
};

// Tiny outlined count tag — "2 FAIL · 1 CHECK", or a quiet "QA ✓".
export const QaTag = ({ areas, prefix }) => {
    const t = qaTally(areas);
    const lvl = t.fail ? 'fail' : t.warn ? 'warn' : 'pass';
    const body = t.fail || t.warn
        ? [t.fail ? `${t.fail} FAIL` : '', t.warn ? `${t.warn} CHECK` : ''].filter(Boolean).join(' · ')
        : 'QA ✓';
    const color = lvl === 'pass' ? '#94a3b8' : TONE[lvl].color;
    return (
        <View style={{ borderWidth: 1, borderStyle: 'solid', borderColor: lvl === 'pass' ? '#cbd5e1' : color, borderRadius: 3, paddingLeft: 4, paddingRight: 4, alignSelf: 'flex-start' }}>
            <Text style={{ fontSize: 9, fontWeight: '700', letterSpacing: 0.4, color, whiteSpace: 'nowrap' }}>{prefix ? `${prefix} ` : ''}{body}</Text>
        </View>
    );
};

// One finding with its detail lines, capped until expanded.
const Finding = ({ it }) => {
    const [all, setAll] = useState(false);
    const lines = it.lines || [];
    const shown = all ? lines : lines.slice(0, 6);
    return (
        <View style={{ marginTop: 2 }}>
            <Text style={{ fontSize: 11.5, color: TONE[it.level].color, fontWeight: it.level === 'fail' ? '600' : '400' }}>
                {it.level === 'info' ? '' : `${TONE[it.level].glyph} `}{it.text}
            </Text>
            {shown.map((l, j) => (
                <Text key={j} style={{ fontSize: 10.5, color: '#64748b', paddingLeft: 12, marginTop: 1 }}>{l}</Text>
            ))}
            {lines.length > 6 ? (
                <Pressable onPress={() => setAll(!all)}>
                    <Text style={{ fontSize: 10.5, color: '#0f172a', fontWeight: '600', paddingLeft: 12, marginTop: 1 }}>
                        {all ? 'Show fewer' : `+ ${lines.length - 6} more`}
                    </Text>
                </Pressable>
            ) : null}
        </View>
    );
};

/**
 * The four areas as one compact block — a summary's open row and a time
 * sheet preview. Each area is one line (its worst level and how many
 * findings) that opens to the findings, so a busy day doesn't push the
 * ticket off the screen.
 */
export const QaFindings = ({ areas, note }) => {
    const [open, setOpen] = useState({});
    return (
        <View>
            <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
                <Text style={{ fontSize: 10, fontWeight: '700', color: '#475569', letterSpacing: 0.4, marginRight: 8 }}>LABOR QA</Text>
                {areas ? <QaTag areas={areas} /> : null}
                {note ? <Text style={{ fontSize: 10.5, color: '#94a3b8', marginLeft: 8 }}>{note}</Text> : null}
            </View>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
                {(areas || []).map((a) => {
                    const tone = TONE[a.status];
                    const n = a.items.filter((x) => x.level === 'fail' || x.level === 'warn').length;
                    const isOpen = Boolean(open[a.id]);
                    return (
                        <Pressable
                            key={a.id}
                            onPress={() => setOpen((o) => ({ ...o, [a.id]: !o[a.id] }))}
                            title={isOpen ? 'Hide the findings' : a.desc}
                            style={{ flexDirection: 'row', alignItems: 'baseline', marginRight: 18, marginBottom: 4, cursor: 'pointer' }}
                        >
                            <Text style={{ fontSize: 10, color: '#94a3b8', width: 11 }}>{isOpen ? '▾' : '▸'}</Text>
                            <Text style={{ fontSize: 11.5, fontWeight: '700', color: tone.color, marginRight: 4 }}>{tone.glyph}</Text>
                            <Text style={{ fontSize: 11.5, fontWeight: isOpen ? '700' : '600', color: '#0f172a' }}>{a.title}</Text>
                            {n ? <Text style={{ fontSize: 10, fontWeight: '700', color: tone.color, marginLeft: 5 }}>{n}</Text> : null}
                        </Pressable>
                    );
                })}
            </View>
            {(areas || []).filter((a) => open[a.id]).map((a) => (
                <View key={a.id} style={{ borderLeftWidth: 2, borderColor: '#e2e8f0', paddingLeft: 10, marginTop: 2, marginBottom: 6 }}>
                    <Text style={{ fontSize: 10, fontWeight: '700', color: '#475569', letterSpacing: 0.4 }}>{a.title.toUpperCase()}</Text>
                    {a.items.map((it, k) => <Finding key={k} it={it} />)}
                </View>
            ))}
        </View>
    );
};
